/**
 * bizlinkSheet — the bank batch as the Excel file Bizlink actually takes.
 *
 * THE TEMPLATE IS A .XLS, NOT A CSV. What gets uploaded to the Bizlink site
 * is the FORMULA sheet saved as Excel 97 ("FRI SEP 11THU SUBCONTRACTORS.xls"),
 * and its cells are typed: C1 is a real date shown as "September 11, 2026",
 * E1 a real time shown as "06:00 PM", the amounts numbers shown with
 * thousands separators, and the account numbers TEXT — "0616295112" keeps
 * its leading zero only because the cell is a string. A CSV opened in Excel
 * loses that zero and gets the batch rejected, which is why the CSV download
 * stays as a fallback and this is the button people press.
 *
 * Cell for cell from the template read on 2026-09-11, column widths
 * included, so the file that comes out of the app opens looking like the
 * one that came out of the workbook. Sheet name "Sheet1", as the template.
 */
import { utils, write, type WorkBook, type WorkSheet } from "xlsx";
import type { IBizlinkBatch } from "./bizlink";

/** Number formats the template's cells carry. */
export const BIZLINK_FORMATS = {
  date: "[$-409]mmmm\\ d\\,\\ yyyy;@",
  time: "hh:mm\\ AM/PM",
  amount: "#,##0.00",
  account: "0_);\\(0\\)",
} as const;

/** Column widths (characters) as the template has them, A–K. */
const COL_WIDTHS = [17.4, 29, 20, 13.9, 33.6, 15.3, 13.9, 14.3, 12, 20, 15.4];

/** "2026-09-11" → Excel serial day (46276). */
export function isoToExcelSerial(iso: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return NaN;
  return Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000) + 25569;
}

/** "18:00" → fraction of a day (0.75). */
export function timeToExcelFraction(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  if (!m) return NaN;
  return (Number(m[1]) * 60 + Number(m[2])) / 1440;
}

/** The sheet, typed cell by typed cell. */
export function bizlinkWorksheet(batch: IBizlinkBatch): WorkSheet {
  const ws: WorkSheet = {};
  const put = (addr: string, cell: { t: "s" | "n"; v: string | number; z?: string }) => { ws[addr] = cell; };

  put("A1", { t: "s", v: "H" });
  put("B1", { t: "s", v: "Payroll Date" });
  put("C1", { t: "n", v: isoToExcelSerial(batch.payrollDate), z: BIZLINK_FORMATS.date });
  put("D1", { t: "s", v: "Payroll Time" });
  put("E1", { t: "n", v: timeToExcelFraction(batch.payrollTime), z: BIZLINK_FORMATS.time });
  put("F1", { t: "s", v: "Total Amount" });
  put("G1", { t: "n", v: batch.totalAmount, z: BIZLINK_FORMATS.amount });
  put("H1", { t: "s", v: "Total Count" });
  put("I1", { t: "n", v: batch.totalCount });
  put("J1", { t: "s", v: "FUNDING ACCOUNT" });
  put("K1", { t: "s", v: batch.fundingAccount });

  ["DETAIL CONSTANT", "EMPLOYEE NAME", "EMPLOYEE ACCOUNT", "AMOUNT", "REMARKS"].forEach((h, i) => {
    put(`${String.fromCharCode(65 + i)}2`, { t: "s", v: h });
  });

  batch.rows.forEach((r, i) => {
    const n = i + 3;
    put(`A${n}`, { t: "s", v: r.constant });
    put(`B${n}`, { t: "s", v: r.employeeName });
    // Text, always: an account number is an identifier, and a leading zero
    // is part of it.
    put(`C${n}`, { t: "s", v: r.employeeAccount, z: BIZLINK_FORMATS.account });
    put(`D${n}`, { t: "n", v: r.amount, z: BIZLINK_FORMATS.amount });
    put(`E${n}`, { t: "s", v: r.remarks });
  });

  const lastRow = Math.max(2, batch.rows.length + 2);
  ws["!ref"] = `A1:K${lastRow}`;
  ws["!cols"] = COL_WIDTHS.map(wch => ({ wch }));
  return ws;
}

export function bizlinkWorkbook(batch: IBizlinkBatch): WorkBook {
  const wb = utils.book_new();
  utils.book_append_sheet(wb, bizlinkWorksheet(batch), "Sheet1");
  return wb;
}

/** The .xls bytes (BIFF8 — the template's own format). */
export function bizlinkXlsBytes(batch: IBizlinkBatch): Uint8Array<ArrayBuffer> {
  // Copied into a fresh buffer so the result is a BlobPart as far as the DOM
  // typings are concerned (SheetJS types its output over ArrayBufferLike).
  const out = write(bizlinkWorkbook(batch), { bookType: "biff8", type: "array" }) as ArrayLike<number>;
  return new Uint8Array(out);
}

/** "BIZLINK-PAYOUT-2026-09-11.xls" — beside the CSV of the same name. */
export function bizlinkXlsFileName(batch: IBizlinkBatch): string {
  return `BIZLINK-PAYOUT-${batch.payrollDate}.xls`;
}
